import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, HelpCircle, Calendar, Clock, MapPin, Users, UserCheck, UserX, CalendarPlus } from 'lucide-react';
import { Event, Guest } from '@/types/event';
import { eventService } from '@/services/eventService';
import { analyticsService } from '@/services/analyticsService';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { CustomRSVPForm } from './CustomRSVPForm';
import { downloadIcsForEvent, buildIcsContent } from '@/lib/icsGenerator';

interface EventRSVPProps {
  event: Event;
  onRSVPSubmitted: () => void;
}

type RsvpResponse = 'accept' | 'decline' | 'maybe';

const triggerHaptic = () => {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(10); } catch { /* noop */ }
  }
};

export const EventRSVP = ({ event, onRSVPSubmitted }: EventRSVPProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentGuest, setCurrentGuest] = useState<Guest | null>(null);
  const [rsvpStats, setRSVPStats] = useState({ accepted: 0, declined: 0, maybe: 0, pending: 0 });
  const [showCustomForm, setShowCustomForm] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.email) {
      const guest = event.guests.find(g => g.email === user.email);
      setCurrentGuest(guest || null);
    }
  }, [event.guests, user]);

  useEffect(() => {
    const acceptOption = event.options.find(opt => opt.value === 'accept');
    const declineOption = event.options.find(opt => opt.value === 'decline');
    const maybeOption = event.options.find(opt => opt.value === 'maybe');

    const acceptedVotes = acceptOption ? acceptOption.votes : 0;
    const declinedVotes = declineOption ? declineOption.votes : 0;
    const maybeVotes = maybeOption ? maybeOption.votes : 0;
    const pending = event.guests.length - acceptedVotes - declinedVotes - maybeVotes;

    setRSVPStats({ accepted: acceptedVotes, declined: declinedVotes, maybe: maybeVotes, pending });
  }, [event.guests, event.options]);

  const handleRSVP = async (response: RsvpResponse) => {
    if (!currentGuest) {
      toast.error('Guest not found');
      return;
    }

    if (currentGuest.hasVoted) {
      toast.error('You have already responded to this invitation');
      return;
    }

    setIsSubmitting(true);
    triggerHaptic();
    try {
      await eventService.submitVotes(event.id, currentGuest.id, [
        { optionId: 'rsvp-dummy', voteType: response }
      ]);

      analyticsService.trackVote(event.id, user?.id, currentGuest.id);

      const labels: Record<RsvpResponse, string> = {
        accept: 'accepted',
        decline: 'declined',
        maybe: 'marked as maybe',
      };

      // For Accept: offer the calendar download via toast action.
      if (response === 'accept') {
        const hasIcs = !!buildIcsContent(event);
        if (hasIcs) {
          toast.success('RSVP accepted!', {
            description: 'Add this event to your calendar so you don\'t forget.',
            action: {
              label: 'Add to calendar',
              onClick: () => downloadIcsForEvent(event),
            },
            duration: 8000,
          });
        } else {
          toast.success('RSVP accepted successfully!');
        }
        setShowCustomForm(true);
      } else {
        toast.success(`RSVP ${labels[response]} successfully!`);
        onRSVPSubmitted();
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      toast.error('Failed to submit RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasResponded = currentGuest?.hasVoted;
  const currentResponse = hasResponded
    ? (event.options.find(opt => opt.voters.includes(currentGuest!.id))?.value)
    : null;

  const handleCustomFormSubmitted = () => {
    setShowCustomForm(false);
    onRSVPSubmitted();
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{event.name}</CardTitle>
          <CardDescription>{event.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {event.rsvpDate && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>{event.rsvpDate}</span>
              </div>
            )}
            {event.rsvpTime && (
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span>{event.rsvpTime}</span>
              </div>
            )}
            {event.rsvpLocation && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{event.rsvpLocation}</span>
              </div>
            )}
          </div>

          <p className="text-sm text-muted-foreground">
            Hosted by {event.hostName}
          </p>
        </CardContent>
      </Card>

      {currentGuest && (
        <Card>
          <CardHeader>
            <CardTitle>Your RSVP</CardTitle>
            <CardDescription>
              {hasResponded
                ? currentResponse === 'accept'
                  ? 'You have accepted this invitation'
                  : currentResponse === 'maybe'
                    ? 'You marked this as maybe'
                    : 'You have declined this invitation'
                : 'Please respond to this invitation'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasResponded ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {currentResponse === 'accept' && (
                  <Badge variant="default" className="bg-success/15 text-success border border-success/30 w-fit">
                    <Check className="h-4 w-4 mr-1" />
                    Accepted
                  </Badge>
                )}
                {currentResponse === 'maybe' && (
                  <Badge className="bg-warning/15 text-warning-foreground border border-warning/40 w-fit">
                    <HelpCircle className="h-4 w-4 mr-1" />
                    Maybe
                  </Badge>
                )}
                {currentResponse === 'decline' && (
                  <Badge variant="secondary" className="bg-destructive/15 text-destructive border border-destructive/30 w-fit">
                    <X className="h-4 w-4 mr-1" />
                    Declined
                  </Badge>
                )}
                <span className="text-sm text-muted-foreground">
                  Thank you for responding to this invitation.
                </span>

                {currentResponse === 'accept' && buildIcsContent(event) && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="sm:ml-auto min-h-[44px]"
                    onClick={() => downloadIcsForEvent(event)}
                  >
                    <CalendarPlus className="h-4 w-4 mr-2" />
                    Add to calendar
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button
                    onClick={() => handleRSVP('accept')}
                    disabled={isSubmitting}
                    className="min-h-[52px] touch-manipulation"
                    aria-label="Accept invitation"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Accept
                  </Button>
                  <Button
                    onClick={() => handleRSVP('maybe')}
                    disabled={isSubmitting}
                    className="min-h-[52px] touch-manipulation bg-warning text-warning-foreground hover:bg-warning/90"
                    aria-label="Mark as maybe"
                  >
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Maybe
                  </Button>
                  <Button
                    onClick={() => handleRSVP('decline')}
                    disabled={isSubmitting}
                    variant="outline"
                    className="min-h-[52px] touch-manipulation"
                    aria-label="Decline invitation"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Decline
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  Not sure yet? Pick <strong>Maybe</strong> — the host will know you're interested but tentative.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Show custom RSVP form after accepting */}
      {showCustomForm && currentGuest && (
        <CustomRSVPForm
          eventId={event.id}
          guestId={currentGuest.id}
          guestEmail={currentGuest.email}
          onSubmitted={handleCustomFormSubmitted}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>RSVP Summary</CardTitle>
          <CardDescription>Response status from all invited guests</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Total</span>
              </div>
              <div className="text-2xl font-bold">{event.guests.length}</div>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <UserCheck className="h-4 w-4 text-success" />
                <span className="text-sm font-medium">Accepted</span>
              </div>
              <div className="text-2xl font-bold text-success">{rsvpStats.accepted}</div>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <HelpCircle className="h-4 w-4 text-warning" />
                <span className="text-sm font-medium">Maybe</span>
              </div>
              <div className="text-2xl font-bold text-warning">{rsvpStats.maybe}</div>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <UserX className="h-4 w-4 text-destructive" />
                <span className="text-sm font-medium">Declined</span>
              </div>
              <div className="text-2xl font-bold text-destructive">{rsvpStats.declined}</div>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Pending</span>
              </div>
              <div className="text-2xl font-bold text-muted-foreground">{rsvpStats.pending}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {event.guests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Guest List</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {event.guests.map((guest) => {
                const guestResponse = guest.hasVoted
                  ? (event.options.find(opt => opt.voters.includes(guest.id))?.value)
                  : 'pending';

                return (
                  <div key={guest.id} className="flex items-center justify-between p-3 border rounded">
                    <div>
                      <div className="font-medium">{guest.name || 'Guest'}</div>
                    </div>
                    <div>
                      {guestResponse === 'accept' && (
                        <Badge className="bg-success/15 text-success border border-success/30">
                          <Check className="h-3 w-3 mr-1" />
                          Accepted
                        </Badge>
                      )}
                      {guestResponse === 'maybe' && (
                        <Badge className="bg-warning/15 text-warning-foreground border border-warning/40">
                          <HelpCircle className="h-3 w-3 mr-1" />
                          Maybe
                        </Badge>
                      )}
                      {guestResponse === 'decline' && (
                        <Badge variant="secondary" className="bg-destructive/15 text-destructive border border-destructive/30">
                          <X className="h-3 w-3 mr-1" />
                          Declined
                        </Badge>
                      )}
                      {guestResponse === 'pending' && (
                        <Badge variant="outline">
                          <Clock className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
